import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Users, TrendingUp, TrendingDown, Briefcase, Plus, Trash2, Edit2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
import Card, { CardHeader, CardBody } from '../ui/Card';
import { CompetitorCreate, CompetitorResponse, CompetitorFinancials } from '../../types/threePhase';
import { standaloneCompaniesAPI } from '../../lib/api';

// Validation schema
const competitorSchema = z.object({
  name: z.string().min(2, 'Competitor name must be at least 2 characters'),
  marketShare: z.number().min(0).max(100).optional(),
  strengths: z.array(z.string()).min(1, 'Add at least one strength'),
  weaknesses: z.array(z.string()).min(1, 'Add at least one weakness'),
  keyProducts: z.array(z.string()).min(1, 'Add at least one key product'),
  financials: z.object({
    revenue: z.number().positive().optional(),
    market_cap: z.number().positive().optional(),
    employees: z.number().int().positive().optional(),
    growth_rate: z.number().optional(),
  }).optional(),
});

interface CompetitorAnalysisFormProps {
  caseId: string;
}

const CompetitorAnalysisForm: React.FC<CompetitorAnalysisFormProps> = ({ caseId }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorResponse | null>(null);

  const [strengthInput, setStrengthInput] = useState('');
  const [weaknessInput, setWeaknessInput] = useState('');
  const [productInput, setProductInput] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CompetitorCreate>({
    resolver: zodResolver(competitorSchema),
    defaultValues: {
      name: '',
      marketShare: undefined,
      strengths: [],
      weaknesses: [],
      keyProducts: [],
      financials: {
        revenue: undefined,
        market_cap: undefined,
        employees: undefined,
        growth_rate: undefined,
      },
    },
  });

  const strengths = watch('strengths') || [];
  const weaknesses = watch('weaknesses') || [];
  const keyProducts = watch('keyProducts') || [];

  // Fetch all competitors
  const { data: competitors, isLoading } = useQuery({
    queryKey: ['competitors', caseId],
    queryFn: async () => {
      const response = await standaloneCompaniesAPI.getAllCompetitors(caseId);
      return response as CompetitorResponse[];
    },
    enabled: !!caseId,
  });

  // Create competitor mutation
  const createMutation = useMutation({
    mutationFn: (data: CompetitorCreate) => standaloneCompaniesAPI.createCompetitor(caseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitors', caseId] });
      toast.success('Competitor added successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add competitor');
    },
  });

  // Update competitor mutation
  const updateMutation = useMutation({
    mutationFn: ({ competitorId, data }: { competitorId: string; data: CompetitorCreate }) =>
      standaloneCompaniesAPI.updateCompetitor(caseId, competitorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitors', caseId] });
      toast.success('Competitor updated successfully');
      resetForm();
      setIsEditing(false);
      setEditingCompetitor(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update competitor');
    },
  });

  // Delete competitor mutation
  const deleteMutation = useMutation({
    mutationFn: (competitorId: string) => standaloneCompaniesAPI.deleteCompetitor(caseId, competitorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitors', caseId] });
      toast.success('Competitor deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete competitor');
    },
  });

  const resetForm = () => {
    reset({
      name: '',
      marketShare: undefined,
      strengths: [],
      weaknesses: [],
      keyProducts: [],
      financials: {
        revenue: undefined,
        market_cap: undefined,
        employees: undefined,
        growth_rate: undefined,
      },
    });
    setStrengthInput('');
    setWeaknessInput('');
    setProductInput('');
  };

  const handleEdit = (competitor: CompetitorResponse) => {
    setIsEditing(true);
    setEditingCompetitor(competitor);
    setValue('name', competitor.name);
    setValue('marketShare', competitor.marketShare);
    setValue('strengths', competitor.strengths);
    setValue('weaknesses', competitor.weaknesses);
    setValue('keyProducts', competitor.keyProducts);
    if (competitor.financials) {
      setValue('financials', competitor.financials);
    }
  };

  const handleDelete = (competitorId: string) => {
    if (confirm('Are you sure you want to delete this competitor?')) {
      deleteMutation.mutate(competitorId);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingCompetitor(null);
    resetForm();
  };

  const onSubmit = (data: CompetitorCreate) => {
    if (isEditing && editingCompetitor) {
      updateMutation.mutate({ competitorId: editingCompetitor.competitorId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const addStrength = () => {
    if (strengthInput.trim()) {
      setValue('strengths', [...strengths, strengthInput.trim()]);
      setStrengthInput('');
    }
  };

  const removeStrength = (index: number) => {
    setValue('strengths', strengths.filter((_, i) => i !== index));
  };

  const addWeakness = () => {
    if (weaknessInput.trim()) {
      setValue('weaknesses', [...weaknesses, weaknessInput.trim()]);
      setWeaknessInput('');
    }
  };

  const removeWeakness = (index: number) => {
    setValue('weaknesses', weaknesses.filter((_, i) => i !== index));
  };

  const addProduct = () => {
    if (productInput.trim()) {
      setValue('keyProducts', [...keyProducts, productInput.trim()]);
      setProductInput('');
    }
  };

  const removeProduct = (index: number) => {
    setValue('keyProducts', keyProducts.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      {/* Existing Competitors List */}
      {competitors && competitors.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary-500" />
                <h3 className="text-lg font-semibold">Identified Competitors ({competitors.length})</h3>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              {competitors.map((competitor) => (
                <div
                  key={competitor.competitorId}
                  className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                >
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900">{competitor.name}</h4>
                    {competitor.marketShare && (
                      <p className="text-sm text-neutral-600">Market Share: {competitor.marketShare}%</p>
                    )}
                    <div className="flex gap-4 mt-2 text-sm">
                      <span className="text-green-600">
                        <TrendingUp className="h-4 w-4 inline mr-1" />
                        {competitor.strengths.length} strengths
                      </span>
                      <span className="text-red-600">
                        <TrendingDown className="h-4 w-4 inline mr-1" />
                        {competitor.weaknesses.length} weaknesses
                      </span>
                      <span className="text-blue-600">
                        <Briefcase className="h-4 w-4 inline mr-1" />
                        {competitor.keyProducts.length} products
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(competitor)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(competitor.competitorId)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Add/Edit Competitor Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary-500" />
              <h3 className="text-lg font-semibold">
                {isEditing ? 'Edit Competitor' : 'Add New Competitor'}
              </h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Competitor Name"
                placeholder="e.g., Competitor Inc."
                {...register('name')}
                error={errors.name?.message}
                required
              />

              <Input
                label="Market Share (%)"
                type="number"
                step="0.1"
                placeholder="25.0"
                {...register('marketShare', { valueAsNumber: true })}
                error={errors.marketShare?.message}
              />
            </div>

            {/* Strengths */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Strengths <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a strength..."
                  value={strengthInput}
                  onChange={(e) => setStrengthInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStrength())}
                />
                <Button type="button" onClick={addStrength} variant="secondary">
                  Add
                </Button>
              </div>
              {errors.strengths && (
                <p className="text-sm text-red-500">{errors.strengths.message}</p>
              )}
              {strengths.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {strengths.map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => removeStrength(index)}
                        className="text-green-500 hover:text-green-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Weaknesses */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Weaknesses <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a weakness..."
                  value={weaknessInput}
                  onChange={(e) => setWeaknessInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addWeakness())}
                />
                <Button type="button" onClick={addWeakness} variant="secondary">
                  Add
                </Button>
              </div>
              {errors.weaknesses && (
                <p className="text-sm text-red-500">{errors.weaknesses.message}</p>
              )}
              {weaknesses.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {weaknesses.map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => removeWeakness(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Key Products */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Key Products <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a product..."
                  value={productInput}
                  onChange={(e) => setProductInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addProduct())}
                />
                <Button type="button" onClick={addProduct} variant="secondary">
                  Add
                </Button>
              </div>
              {errors.keyProducts && (
                <p className="text-sm text-red-500">{errors.keyProducts.message}</p>
              )}
              {keyProducts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {keyProducts.map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Optional Financials */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-neutral-700 mb-3">
                Financial Information (Optional)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Revenue (USD)"
                  type="number"
                  placeholder="1000000000"
                  {...register('financials.revenue', { valueAsNumber: true })}
                />
                <Input
                  label="Market Cap (USD)"
                  type="number"
                  placeholder="5000000000"
                  {...register('financials.market_cap', { valueAsNumber: true })}
                />
                <Input
                  label="Employees"
                  type="number"
                  placeholder="10000"
                  {...register('financials.employees', { valueAsNumber: true })}
                />
                <Input
                  label="Growth Rate (%)"
                  type="number"
                  step="0.1"
                  placeholder="12.5"
                  {...register('financials.growth_rate', { valueAsNumber: true })}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          {isEditing && (
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            loading={createMutation.isPending || updateMutation.isPending}
          >
            {isEditing ? 'Update Competitor' : 'Add Competitor'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompetitorAnalysisForm;
